/* =====================================================
   CWC FEATURED PRODUCT JAVASCRIPT - COMPLETE FIXED
   =====================================================
   
   Purpose: Handles product variants, pricing, subscriptions, 
   add-to-cart functionality, and FAQ interactions.
   
   FIXES IN THIS VERSION:
   1. Correct price display selectors matching Liquid
   2. Correct subscription checkbox selector
   3. Button prices update with variant/subscription changes
   4. Savings calculation includes TOTAL savings (compare_at + subscription)
   5. Savings display respects savingsDisplayType setting (dollar/percentage)
   6. Partial option selection support
   
   SECTION DATA ATTRIBUTES:
   - data-section-id="{{ section.id }}"
   - data-product-id="{{ product.id }}"
   - data-variants="{{ product.variants | json | escape }}"
   - data-selling-plans="{{ product.selling_plan_groups | json | escape }}"
   - data-savings-display="{{ section.settings.savings_display_type }}"
   
   ===================================================== */

/* =====================================================
   INITIALIZATION
   ===================================================== */
document.addEventListener("DOMContentLoaded", function () {
  const sections = document.querySelectorAll(".cwc-featured-product");

  sections.forEach((section) => {
    const sectionId = section.dataset.sectionId;
    const productId = section.dataset.productId;
    const variants = JSON.parse(section.dataset.variants || "[]");
    const sellingPlanGroups = JSON.parse(section.dataset.sellingPlans || "[]");
    const savingsDisplayType = section.dataset.savingsDisplay || "dollar";

    initFeaturedProduct(
      section,
      sectionId,
      variants,
      sellingPlanGroups,
      savingsDisplayType
    );
  });
});

/* =====================================================
   MAIN INITIALIZATION FUNCTION
   ===================================================== */
function initFeaturedProduct(
  section,
  sectionId,
  variants,
  sellingPlanGroups,
  savingsDisplayType
) {
  // console.log("=== Initializing Featured Product ===");
  // console.log("Section ID:", sectionId);
  // console.log("Variants:", variants.length);
  // console.log("Selling Plan Groups:", sellingPlanGroups.length);
  // console.log("Savings Display Type:", savingsDisplayType);

  /* -----------------------------------------------------
     DOM ELEMENT REFERENCES
     ----------------------------------------------------- */
  // Form elements
  const form = section.querySelector(`#product-form-${sectionId}`);
  const variantIdInput = section.querySelector(".variant-id-input");
  const sellingPlanInput = section.querySelector(".selling-plan-input");

  // Option buttons and inputs
  const optionButtons = section.querySelectorAll(
    ".cwc-featured-product__option_button, .cwc-featured-product__option_type_button, .cwc-featured-product__option_size_button"
  );
  const optionInputs = section.querySelectorAll(
    "input[data-option-index]:not(.variant-id-input):not(.selling-plan-input)"
  );

  // Subscription checkbox (id="auto-refill-{{ section.id }}")
  const autoRefillCheckbox = section.querySelector(`#auto-refill-${sectionId}`);

  // Add to cart button
  const addToCartButton = section.querySelector(`#add-to-cart-${sectionId}`);

  // Price display block (class="cwc-featured-product__price")
  const priceDisplay = section.querySelector(".cwc-featured-product__price");

  // Button price elements
  const buttonPriceElement = section.querySelector(
    `#current-price-button-${sectionId}`
  );
  const buttonCompareElement = section.querySelector(
    `#compare-price-button-${sectionId}`
  );

  /* -----------------------------------------------------
     VALIDATION & DEBUG
     ----------------------------------------------------- */
  if (!form || !variantIdInput) {
    console.warn("Required elements not found for section", sectionId);
    return;
  }

  // console.log("Found elements:", {
  //   form: !!form,
  //   variantIdInput: !!variantIdInput,
  //   sellingPlanInput: !!sellingPlanInput,
  //   optionInputs: optionInputs.length,
  //   optionButtons: optionButtons.length,
  //   autoRefillCheckbox: !!autoRefillCheckbox,
  //   priceDisplay: !!priceDisplay,
  //   addToCartButton: !!addToCartButton,
  //   buttonPriceElement: !!buttonPriceElement,
  //   buttonCompareElement: !!buttonCompareElement,
  // });

  /* =====================================================
     SELLING PLAN MANAGEMENT
     ===================================================== */
  // Use existing selling plan input from form
  const finalSellingPlanInput = sellingPlanInput;

  // Get the selling plan ID from the product's selling plan groups
  function getSellingPlanId() {
    if (
      sellingPlanGroups.length > 0 &&
      sellingPlanGroups[0]?.selling_plans?.length > 0
    ) {
      return (
        sellingPlanGroups[0].selling_plans[0].id ||
        sellingPlanGroups[0].selling_plans[0]
      );
    }
    return null;
  }

  function updateSellingPlan() {
    if (!finalSellingPlanInput) return;

    const isAutoRefill = autoRefillCheckbox && autoRefillCheckbox.checked;
    const sellingPlanId = getSellingPlanId();

    if (isAutoRefill && sellingPlanId) {
      finalSellingPlanInput.value = sellingPlanId;
      // console.log("Selling plan set:", sellingPlanId);
    } else {
      finalSellingPlanInput.value = "";
      // console.log("Selling plan cleared");
    }
  }

  /* =====================================================
     VARIANT FINDING FUNCTIONS
     ===================================================== */
  function findVariantById(variantId) {
    return variants.find((v) => v.id == variantId);
  }

  /* =====================================================
     PRICING FUNCTIONS
     ===================================================== */
  function formatPrice(priceInCents) {
    return (priceInCents / 100).toFixed(2);
  }

  /**
   * Get pricing data for display
   *
   * Scenarios:
   * 1. Subscription ON:
   *    - price = subscription price (per_delivery_price)
   *    - compareAtPrice = original compare_at_price (shows TOTAL savings)
   *
   * 2. Subscription OFF with compare_at_price:
   *    - price = regular variant price
   *    - compareAtPrice = variant's compare_at_price
   *
   * 3. Subscription OFF without compare_at_price:
   *    - price = regular variant price
   *    - compareAtPrice = null (no savings shown)
   */
  function getPriceForDisplay(variant) {
    if (!variant) return null;

    const isAutoRefill = autoRefillCheckbox && autoRefillCheckbox.checked;
    const sellingPlanId = finalSellingPlanInput?.value;

    // console.log("getPriceForDisplay:", {
    //   isAutoRefill,
    //   sellingPlanId,
    //   variantPrice: variant.price,
    //   variantCompareAt: variant.compare_at_price,
    //   hasAllocations: !!variant.selling_plan_allocations,
    // });

    // Subscription is ON
    if (isAutoRefill && sellingPlanId && variant.selling_plan_allocations) {
      const allocation = variant.selling_plan_allocations.find(
        (alloc) => String(alloc.selling_plan_id) === String(sellingPlanId)
      );

      if (allocation) {
        // console.log("Found subscription allocation:", allocation);

        // Use compare_at_price if it exists for TOTAL savings display
        // Otherwise use regular price as the compare price
        const comparePrice =
          variant.compare_at_price && variant.compare_at_price > variant.price
            ? variant.compare_at_price
            : variant.price;

        return {
          price: allocation.per_delivery_price,
          compareAtPrice: comparePrice,
          isSubscription: true,
        };
      }
    }

    // One-time purchase (subscription OFF)
    return {
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      isSubscription: false,
    };
  }

  /**
   * Update all price displays (price block + button prices)
   */
  function updatePriceDisplay(variant) {
    if (!variant) {
      console.warn("No variant provided to updatePriceDisplay");
      return;
    }

    const pricing = getPriceForDisplay(variant);
    if (!pricing) return;

    // console.log("Updating price display:", {
    //   currentPrice: pricing.price,
    //   compareAtPrice: pricing.compareAtPrice,
    //   isSubscription: pricing.isSubscription,
    // });

    const hasSavings =
      pricing.compareAtPrice && pricing.compareAtPrice > pricing.price;
    let savingsAmount = 0;
    let savingsPercent = 0;

    if (hasSavings) {
      savingsAmount = pricing.compareAtPrice - pricing.price;
      savingsPercent = Math.round(
        (savingsAmount / pricing.compareAtPrice) * 100
      );

      // console.log("Savings calculation:", {
      //   compareAtPrice: pricing.compareAtPrice,
      //   currentPrice: pricing.price,
      //   savingsAmount: savingsAmount,
      //   savingsPercent: savingsPercent,
      // });
    }

    /* -----------------------------------------------------
       UPDATE PRICE DISPLAY BLOCK
       ----------------------------------------------------- */
    if (priceDisplay) {
      const priceElement = priceDisplay.querySelector(
        ".cwc-featured-product__price-current"
      );
      const compareElement = priceDisplay.querySelector(
        ".cwc-featured-product__price-compare"
      );
      const saveWrapper = priceDisplay.querySelector(
        ".cwc-featured-product__price-save"
      );
      const saveAmountElement = priceDisplay.querySelector(
        ".cwc-featured-product__price-save-amount"
      );

      // Update current price
      if (priceElement) {
        priceElement.textContent = `$${formatPrice(pricing.price)}`;
      }

      if (hasSavings) {
        // Show and update compare price
        if (compareElement) {
          compareElement.textContent = `$${formatPrice(
            pricing.compareAtPrice
          )}`;
          compareElement.style.display = "";
        }

        // Show save wrapper and update amount
        if (saveWrapper) {
          saveWrapper.style.display = "flex";
        }

        if (saveAmountElement) {
          if (savingsDisplayType === "percentage") {
            saveAmountElement.textContent = `${savingsPercent}%`;
          } else {
            saveAmountElement.textContent = `$${formatPrice(savingsAmount)}`;
          }
        }
      } else {
        // No savings - hide compare and save elements
        if (compareElement) {
          compareElement.style.display = "none";
        }
        if (saveWrapper) {
          saveWrapper.style.display = "none";
        }
      }
    }

    /* -----------------------------------------------------
       UPDATE BUTTON PRICES
       ----------------------------------------------------- */
    if (buttonPriceElement) {
      buttonPriceElement.textContent = `$${formatPrice(pricing.price)}`;
    }

    if (buttonCompareElement) {
      if (hasSavings) {
        buttonCompareElement.textContent = `$${formatPrice(
          pricing.compareAtPrice
        )}`;
        buttonCompareElement.style.display = "";
      } else {
        buttonCompareElement.style.display = "none";
      }
    }

    // console.log("Price display update complete");
  }

  /**
   * Update add to cart button state
   */
  function updateAddToCartButton(variant) {
    if (!addToCartButton) return;

    const buttonText = addToCartButton.querySelector(".cwc-button-text");

    if (variant && variant.available) {
      addToCartButton.disabled = false;
      if (buttonText) {
        const originalText = buttonText.dataset.originalText || "Add to Cart";
        // Keep the " | " if it's in the original text
        buttonText.textContent = originalText.includes("|")
          ? originalText
          : originalText + " | ";
      }
    } else {
      addToCartButton.disabled = true;
      if (buttonText) {
        buttonText.textContent = "Sold Out";
      }
    }
  }

  /* =====================================================
     MAIN UPDATE FUNCTION
     ===================================================== */
  function updateVariant(variant = null) {
    // console.log("=== Updating Variant ===");

    if (!variant && optionInputs.length === 0) {
      variant = variants[0] || findVariantById(variantIdInput?.value);
    }

    if (!variant) {
      console.warn("No variant to update");
      return;
    }

    // console.log("Variant:", variant.id, variant.title);

    // Update variant ID input
    if (variantIdInput) {
      variantIdInput.value = variant.id;
    }

    // Update selling plan input FIRST (before price calculation)
    updateSellingPlan();

    // Update all price displays (block + button)
    updatePriceDisplay(variant);

    // Update button state
    updateAddToCartButton(variant);

    // Dispatch event for add-to-cart script
    document.dispatchEvent(
      new CustomEvent("cwc:variant-updated", {
        detail: {
          variant,
          sectionId,
          pricing: getPriceForDisplay(variant),
        },
      })
    );

    // console.log("=== Variant Update Complete ===");
  }

  /* =====================================================
     EVENT LISTENERS - OPTION BUTTONS
     ===================================================== */
  // console.log("Setting up option button listeners:", optionButtons.length);

  optionButtons.forEach((button) => {
    button.addEventListener("click", function () {
      console.log("\n=== Option Button Clicked ===");

      const optionIndex = this.getAttribute("data-option-index");
      const value = this.getAttribute("data-value");

      // console.log("Option Index:", optionIndex);
      // console.log("Value:", value);

      // Update visual state
      const siblings = this.parentNode.querySelectorAll(
        ".cwc-featured-product__option_button, .cwc-featured-product__option_type_button, .cwc-featured-product__option_size_button"
      );
      siblings.forEach((sibling) => sibling.classList.remove("selected"));
      this.classList.add("selected");

      // Update hidden input
      const hiddenInput = section.querySelector(
        `input[data-option-index="${optionIndex}"]`
      );

      if (hiddenInput) {
        // console.log(
        //   `Updating option ${optionIndex}: "${hiddenInput.value}" → "${value}"`
        // );
        hiddenInput.value = value;
      } else {
        console.warn("⚠ Hidden input not found for option index:", optionIndex);
        return;
      }

      // Build complete option array for variant matching
      const currentVariantId = variantIdInput.value;
      const currentVariant = findVariantById(currentVariantId);

      if (!currentVariant) {
        console.error("No current variant found");
        return;
      }

      const totalOptionsInProduct = currentVariant.options.length;
      // console.log("Product has", totalOptionsInProduct, "total options");
      // console.log("Section displays", optionInputs.length, "option inputs");

      const selectedOptions = [];

      for (let i = 0; i < totalOptionsInProduct; i++) {
        const input = Array.from(optionInputs).find(
          (inp) => inp.getAttribute("data-option-index") === String(i)
        );

        if (input) {
          selectedOptions[i] = input.value;
          // console.log(`Position ${i}: Using input value "${input.value}"`);
        } else {
          selectedOptions[i] = currentVariant.options[i];
          // console.log(
          // `Position ${i}: Using current variant value "${currentVariant.options[i]}" (no input)`
          // );
        }
      }

      // console.log("Complete options array:", selectedOptions);

      // Find matching variant
      const variant = variants.find((v) => {
        const isMatch = v.options.every((variantOptionValue, position) => {
          return variantOptionValue === selectedOptions[position];
        });

        if (isMatch) {
          // console.log(`✓ Match found: Variant ${v.id} - ${v.title}`);
        }

        return isMatch;
      });

      if (variant) {
        // console.log("=== Variant Found ===");
        // console.log("ID:", variant.id);
        // console.log("Title:", variant.title);
        // console.log("Options:", variant.options);

        updateVariant(variant);
      } else {
        // console.error("=== ✗ No Variant Found ===");
        // console.log("Searched for:", selectedOptions);
        // console.log("Available variants:");
        variants.forEach((v) => {
          // console.log(`  ${v.id}: [${v.options.join(", ")}]`);
        });

        if (addToCartButton) {
          addToCartButton.disabled = true;
          const buttonText = addToCartButton.querySelector(".cwc-button-text");
          if (buttonText) {
            buttonText.textContent = "Unavailable";
          }
        }
      }

      // console.log("=== End Option Change ===\n");
    });
  });

  /* -----------------------------------------------------
     SUBSCRIPTION CHECKBOX HANDLER
     ----------------------------------------------------- */
  if (autoRefillCheckbox) {
    // console.log("Setting up subscription checkbox handler");

    // Set checkbox as initially checked
    autoRefillCheckbox.checked = true;

    const checkboxIcon = section.querySelector(".cwc-checkbox-icon");
    if (checkboxIcon) {
      checkboxIcon.classList.add("initially-checked");
    }

    // Initialize selling plan on page load
    updateSellingPlan();

    autoRefillCheckbox.addEventListener("change", function () {
      // console.log("\n=== Subscription Toggled ===");
      // console.log("Checked:", this.checked);

      // Update selling plan first
      updateSellingPlan();

      // Then recalculate prices with current variant
      const currentVariantId = variantIdInput.value;
      const currentVariant = findVariantById(currentVariantId);

      // console.log(
      //   "Current variant:",
      //   currentVariant?.id,
      //   currentVariant?.title
      // );

      if (currentVariant) {
        updatePriceDisplay(currentVariant);
      }

      // console.log("=== End Subscription Toggle ===\n");
    });
  } else {
    // console.log("No subscription checkbox found for section", sectionId);
  }

  /* =====================================================
     FAQ FUNCTIONALITY
     ===================================================== */
  function initFAQBlocks() {
    const faqItems = section.querySelectorAll(".cwc-featured-product__faq");
    // console.log(`Found ${faqItems.length} FAQ items`);

    faqItems.forEach((item, index) => {
      const question = item.querySelector(
        ".cwc-featured-product__faq-question"
      );
      const answer = item.querySelector(".cwc-featured-product__faq-answer");
      const icon = item.querySelector(".cwc-featured-product__faq-icon");

      if (!question || !answer) return;

      question.addEventListener("click", () => {
        const isCurrentlyActive = item.classList.contains("active");

        faqItems.forEach((other) => {
          if (other !== item && other.classList.contains("active")) {
            const otherAnswer = other.querySelector(
              ".cwc-featured-product__faq-answer"
            );
            const otherIcon = other.querySelector(
              ".cwc-featured-product__faq-icon"
            );

            otherAnswer.style.maxHeight = otherAnswer.scrollHeight + "px";
            setTimeout(() => {
              otherAnswer.style.maxHeight = "0";
              other.classList.remove("active");
              if (otherIcon) otherIcon.textContent = "+";
            }, 10);
          }
        });

        setTimeout(() => {
          if (isCurrentlyActive) {
            answer.style.maxHeight = answer.scrollHeight + "px";
            setTimeout(() => {
              answer.style.maxHeight = "0";
              item.classList.remove("active");
              if (icon) icon.textContent = "+";
            }, 10);
          } else {
            item.classList.add("active");
            if (icon) icon.textContent = "−";
            answer.style.maxHeight = answer.scrollHeight + "px";

            answer.addEventListener(
              "transitionend",
              () => {
                if (item.classList.contains("active")) {
                  answer.style.maxHeight = "none";
                }
              },
              { once: true }
            );
          }
        }, 50);
      });
    });
  }

  /* =====================================================
     INITIALIZATION COMPLETION
     ===================================================== */
  initFAQBlocks();

  // Initialize with current selection
  const initialVariantId = variantIdInput.value;
  const initialVariant = findVariantById(initialVariantId);
  if (initialVariant) {
    // console.log("Initial variant:", initialVariant.id);
    updateVariant(initialVariant);
  }

  // console.log("=== Initialization Complete ===\n");
}
